import {
  Button,
  ButtonGroup,
  Flex,
  HStack,
  Text,
  useToast,
} from '@chakra-ui/react';
import WarningSharpIcon from '@gamma/ui/icons/WarningSharpIcon';
import { useContext } from 'react';
import { i18n } from '../../../../shared/localization';
import {
  GET_COMMENTS_FOR_PARENT_PAGINATED,
  GetCommentsForParentVariables,
  useCreateComment,
} from '../../queries';
import { DocumentNode } from '@apollo/client';
import { TextArea } from '@gamma/ui/form-fields';
import { GraphQLReqStatus } from '@gamma/ui/progress';
import { Controller, useForm } from 'react-hook-form';
import { SlideDrawerContext } from '../../../../shared/providers';
import { useDetectionContext } from '../../context';

const {
  toasts,
  detection: { addNote, note, close, noteRequired, noteTooLong },
} = i18n.pages.detections;

export interface AddNoteContentProps {
  onClose?: () => void;
  refetchMain?: boolean;
  setIsLoading?: React.Dispatch<React.SetStateAction<boolean>>;
  isLoading?: boolean;
}

interface AddNoteFormValues {
  note: string;
}

export const AddNoteContent = ({
  onClose: closePopup,
}: AddNoteContentProps) => {
  const showToastMessage = useToast();
  const { setIsNotesDrawerLoading } = useContext(SlideDrawerContext);
  const { detectionData } = useDetectionContext();
  const { detection_id, tenant, tenant_info } = detectionData || {};
  const tenant_id = tenant_info?.tenant_id;

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<AddNoteFormValues>();

  const onClose = () => {
    reset();
    closePopup && closePopup();
  };

  const refetchQueries: {
    query: DocumentNode;
    variables: GetCommentsForParentVariables;
  }[] = [
    {
      query: GET_COMMENTS_FOR_PARENT_PAGINATED,
      variables: {
        parent: {
          parent_object_id: detection_id ?? '',
          parent_object_type: 'detection',
        },
        size: 10,
        sort: [{ field: 'created_at', direction: 'desc' }],
      },
    },
  ];

  const [
    createComment,
    { loading: createCommentLoading, error: createCommentError },
  ] = useCreateComment({
    awaitRefetchQueries: true,
    refetchQueries: refetchQueries,
    onCompleted: () => {
      showToastMessage({
        status: 'success',
        title: toasts.success,
        description: toasts.addNoteSuccess,
        isClosable: true,
        position: 'bottom-right',
      });
      onClose && onClose();
      setIsNotesDrawerLoading && setIsNotesDrawerLoading(false);
    },
    onError: () => {
      console.error(createCommentError);
      showToastMessage({
        status: 'error',
        title: toasts.error,
        description: toasts.addNoteError,
        isClosable: true,
        position: 'bottom-right',
      });
      setIsNotesDrawerLoading && setIsNotesDrawerLoading(false);
    },
  });

  const onSubmit = (formValues: AddNoteFormValues) => {
    createComment({
      variables: {
        comment: {
          comment: formValues.note,
          parent: {
            parent_object_type: 'detection',
            parent_object_id: detection_id || '',
          },
          tenant: tenant || tenant_id || '',
        },
      },
    });
    setIsNotesDrawerLoading && setIsNotesDrawerLoading(createCommentLoading);
    reset();
  };

  return createCommentLoading ? (
    <GraphQLReqStatus loading={true} />
  ) : (
    <>
      <Controller
        control={control}
        name="note"
        render={({ field: { onChange, name, value } }) => (
          <TextArea
            onChange={onChange}
            name={name}
            label={note}
            isRequired={true}
            value={value}
            error={!!errors.note?.message}
          />
        )}
        rules={{
          required: noteRequired,
          validate: (value) => {
            if (!value.trim()) return noteRequired;
            if (value.length > 10000) return noteTooLong;
            return true;
          },
        }}
      />
      {errors.note && (
        <HStack marginTop={1} textColor="red.300">
          <WarningSharpIcon />
          <Text>{errors.note.message}</Text>
        </HStack>
      )}
      <Flex justifyContent="end">
        <ButtonGroup marginTop={2}>
          <Button
            variant="solid"
            colorScheme="gray"
            data-testid="notes-close-button"
            onClick={onClose}
            isDisabled={createCommentLoading}
          >
            {close}
          </Button>
          <Button
            variant="solid"
            colorScheme="blue"
            data-testid="submit-note-button"
            onClick={handleSubmit(onSubmit)}
            isDisabled={createCommentLoading}
            isLoading={createCommentLoading}
          >
            {addNote}
          </Button>
        </ButtonGroup>
      </Flex>
    </>
  );
};
